import { PropsWithChildren } from 'react';
import { useFocusRing } from '@react-aria/focus';
import { FocusableCheckboxIcon } from './FocusableCheckboxIcon';
import { CheckboxFieldItemWrapper } from './atoms';
import { Field } from '../Field/Field';
import { CommonFieldProps } from '../Field/types';
import { CommonCheckboxProps } from './types';
import { SpinnerIcon } from '@ardoq/icons';
import { FlexBox } from '@ardoq/layout';
import { HiddenCheckbox } from '../common/HiddenCheckbox';

type CheckboxFieldItemProps = Omit<CommonCheckboxProps, 'isFocused'> & {
  isDisabled?: boolean;
  isLoading?: boolean;
  name?: string;
  onChange?: (newValue: boolean) => void;
  onShiftClick?: () => void;
  stopPropagation?: boolean;
  preventDefault?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  tabIndex?: number;
};

const CheckboxFieldItem = ({
  isDisabled,
  isLoading,
  isChecked,
  isMixed,
  hasError,
  hasWarning,
  children,
  onChange,
  preventDefault,
  stopPropagation,
  onShiftClick,
  isRequired,
  indicator,
  inputRef,
  ...rest
}: PropsWithChildren<CheckboxFieldItemProps>) => {
  const { isFocusVisible, focusProps } = useFocusRing();

  return (
    <CheckboxFieldItemWrapper
      $isDisabled={isDisabled || isLoading}
      $isActive={!isLoading && (Boolean(isChecked) || isMixed)}
      $hasError={!isLoading && hasError}
      $hasWarning={!isLoading && hasWarning}
      onClick={event => {
        /**
         * Make sure to set preventDefault to true when you use onShiftClick,
         * otherwise you get cancelling event when multi-selecting from bottom to top.
         */
        if (event.nativeEvent.shiftKey && onShiftClick) {
          onShiftClick();
        }
      }}
    >
      {isLoading ? (
        <SpinnerIcon />
      ) : (
        <FocusableCheckboxIcon
          isChecked={isChecked}
          isMixed={isMixed}
          isFocused={isFocusVisible}
        />
      )}
      {/* Since CheckboxFieldItemWrapper is a <label> and wraps the hidden checkbox, the
          onClick is triggered regardless of whether it's hidden from the user. */}
      <HiddenCheckbox
        {...focusProps}
        ref={inputRef}
        onClick={event => {
          if (preventDefault) event.preventDefault();
          if (stopPropagation) event.stopPropagation();
        }}
        onChange={event => {
          if (isDisabled || isLoading) return;
          if (preventDefault) event.preventDefault();
          if (stopPropagation) event.stopPropagation();
          onChange?.(!isChecked);
        }}
        checked={isChecked === true}
        aria-disabled={isDisabled || isLoading}
        aria-required={
          isRequired || indicator === 'required' ? 'true' : undefined
        }
        {...rest}
      />
      {children}
    </CheckboxFieldItemWrapper>
  );
};

export type CheckboxProps = CommonFieldProps & CheckboxFieldItemProps;

export const Checkbox = ({
  isDisabled,
  isChecked,
  isMixed,
  indicator,
  hasError,
  hasWarning,
  children,
  onChange,
  errorMessage,
  warningMessage,
  helperText,
  hintMessage,
  label,
  onCloseFieldClick,
  closeFieldIconTooltip,
  dataTestId,
  popoverHelpContent,
  popoverContent,
  onShiftClick,
  inputRef,
  ...rest
}: PropsWithChildren<CheckboxProps>) => (
  <Field
    isDisabled={isDisabled}
    indicator={indicator}
    label={label}
    helperText={helperText}
    onCloseFieldClick={onCloseFieldClick}
    closeFieldIconTooltip={closeFieldIconTooltip}
    hintMessage={hintMessage}
    errorMessage={errorMessage}
    warningMessage={warningMessage}
    dataTestId={dataTestId}
    popoverHelpContent={popoverHelpContent}
    popoverContent={popoverContent}
  >
    <FlexBox>
      <CheckboxFieldItem
        isDisabled={isDisabled}
        hasError={hasError || Boolean(errorMessage)}
        hasWarning={hasWarning || Boolean(warningMessage)}
        isMixed={isMixed}
        isChecked={isChecked}
        onChange={onChange}
        onShiftClick={onShiftClick}
        inputRef={inputRef}
        {...rest}
      >
        {children}
      </CheckboxFieldItem>
    </FlexBox>
  </Field>
);
